import { useTranslation } from 'react-i18next';

// Services
import { DASHBOARD_FILTERS_QUERY_KEY } from '../../../services/dashboard-filters/dashboardFiltersQueries';

// Domains
import useConditionalFilters from '../../../domain/tableConditionalFilter/useConditionalFilters';
import useTablePersonalView from '../../../domain/tablePersonalView/useTablePersonalView';
import useTableSearch from '../../../domain/tableSearch/useTableSearch';

// Store
import { Popover } from '../../../store/popover/PopoverContext';

// Components
import TableFilters, {
  TableFiltersToolbar,
  TableFiltersGroup,
} from '../../molecules/TableFilters/TableFilters';
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import ModalTrigger from '../../molecules/ModalTrigger/ModalTrigger';
import Badge from '../../atoms/Badge/Badge';
import FilterConditionalFilters from '../FilterConditionalFilters/FilterConditionalFilters';
import FilterPersonalViews from '../FilterPersonalViews/FilterPersonalViews';
import FilterNavigationSearch from '../FilterNavigationSearch/FilterNavigationSearch';

/**
 * Dashboard filter bar: conditional filters, personal views and search,
 * built from the table filters blocks. No reset (`/reset-filters` has no dashboard type).
 * @param {object} props
 * @param {ApiManagerDashboardFilters} props.dashboardFilters - Dashboard filters from GET
 * @param {string} props.path - Dashboard path (personal views cache key)
 * @param {string | null} props.contexte - Dashboard context
 * @param {boolean} props.isLoading - True while dashboard filters are loading
 * @param {boolean} props.isSubmitting - True while a dashboard filters POST is pending
 * @param {(partial: Partial<ApiManagerDashboardFiltersCreate>, options?: { onSuccess?: () => void }) => void} props.onSubmit - Posts the changed part
 * @param {(url: string) => void} props.handleOpenModal - Opens a form in the form panel
 * @returns {JSX.Element}
 */
function DashboardFilters({
  dashboardFilters,
  path,
  contexte,
  isLoading,
  isSubmitting,
  onSubmit,
  handleOpenModal,
}) {
  const { t } = useTranslation();

  const conditionalFilters = useConditionalFilters({
    filters: dashboardFilters?.filters,
    initialValues: dashboardFilters?.filterValues,
    isLoading,
  });

  const personalViews = useTablePersonalView({
    personalViews: dashboardFilters?.personalViews,
    isLoading,
  });
  const {
    selectedPersonalViewItem,
    popoverPersonalViewIsOpen,
    setPopoverPersonalViewIsOpen,
  } = personalViews;

  const tableSearch = useTableSearch({
    searchOptions: dashboardFilters?.search?.options,
    initialValue: dashboardFilters?.search?.searchValue,
    initialSelectedOptions: dashboardFilters?.search?.selectedOptions,
    isLoading,
  });

  const conditionalFiltersCount = conditionalFilters.getConditionalFiltersCount(dashboardFilters?.filterValues);
  const hasConditionalFilters = (dashboardFilters?.filters?.length ?? 0) > 0;
  // Keep the popover when there is no view yet but one can be created
  const hasPersonalViews = (dashboardFilters?.personalViews?.options?.length ?? 0) > 0
    || !!dashboardFilters?.personalViewsFormUrl;
  const hasSearch = (dashboardFilters?.search?.options?.length ?? 0) > 0;

  const handleConditionalFiltersSubmit = (/** @type {Array<ApiManagerFilterValues>} */ data) => {
    onSubmit(
      { filterValues: data },
      { onSuccess: () => conditionalFilters.setPopoverConditionalFiltersIsOpen(false) },
    );
  };

  const handlePersonalViewsSelectedOptionMutation = (/** @type {string} */ selectedOption) => {
    onSubmit(
      { personalViews: { selectedOption } },
      { onSuccess: () => setPopoverPersonalViewIsOpen(false) },
    );
  };

  const handleSearchMutation = () => {
    onSubmit({
      search: {
        searchValue: tableSearch.tableSearch,
        selectedOptions: tableSearch.selectedColumnsArray,
      },
    });
  };

  return (
    <TableFilters>
      <TableFiltersToolbar>
        <TableFiltersGroup>
          {hasConditionalFilters && (
            <Popover
              open={conditionalFilters.popoverConditionalFiltersIsOpen}
              onOpenChange={conditionalFilters.setPopoverConditionalFiltersIsOpen}
              placement="bottom-start"
            >
              <PopoverTrigger asChild>
                <ModalTrigger
                  icon="filter"
                  title={t('table.filters.others')}
                  label={t('table.filters.others')}
                  onClick={() => conditionalFilters.setPopoverConditionalFiltersIsOpen(
                    !conditionalFilters.popoverConditionalFiltersIsOpen,
                  )}
                  maxWidth="150px"
                  isLoading={isLoading}
                >
                  {conditionalFiltersCount > 0 ? <Badge value={conditionalFiltersCount} /> : null}
                </ModalTrigger>
              </PopoverTrigger>
              <PopoverContent>
                <FilterConditionalFilters
                  isLoading={isLoading}
                  isSubmitting={isSubmitting}
                  onSubmit={handleConditionalFiltersSubmit}
                  conditionalFilters={conditionalFilters}
                />
              </PopoverContent>
            </Popover>
          )}

          {hasPersonalViews && (
            <Popover
              open={popoverPersonalViewIsOpen}
              onOpenChange={setPopoverPersonalViewIsOpen}
              placement="bottom-start"
            >
              <PopoverTrigger asChild>
                <ModalTrigger
                  icon={popoverPersonalViewIsOpen ? 'unfold-less' : 'unfold-more'}
                  iconPosition="right"
                  title={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                  label={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                  onClick={() => setPopoverPersonalViewIsOpen(!popoverPersonalViewIsOpen)}
                  isLoading={isLoading}
                  maxWidth="150px"
                />
              </PopoverTrigger>
              <PopoverContent>
                <FilterPersonalViews
                  mutationKey={DASHBOARD_FILTERS_QUERY_KEY}
                  path={path}
                  personalViews={personalViews}
                  personalViewsPath={dashboardFilters?.personalViewsPath}
                  personalViewsFormUrl={dashboardFilters?.personalViewsFormUrl}
                  handlePersonalViewsSelectedOptionMutation={handlePersonalViewsSelectedOptionMutation}
                  handleOpenModal={handleOpenModal}
                  managerPersonalViews={dashboardFilters?.personalViews}
                  contexte={contexte}
                />
              </PopoverContent>
            </Popover>
          )}
        </TableFiltersGroup>

        {hasSearch && (
          <TableFiltersGroup>
            <FilterNavigationSearch
              tableSearchDomain={tableSearch}
              searchValue={dashboardFilters?.search?.searchValue}
              options={dashboardFilters?.search?.options}
              isLoading={isLoading}
              handleSearchMutation={handleSearchMutation}
            />
          </TableFiltersGroup>
        )}
      </TableFiltersToolbar>
    </TableFilters>
  );
}

export default DashboardFilters;
